/**
 * NextJS-compatible React hook for managing cross-domain session state
 * Provides unified interface for cross-domain session management
 */

"use client";

import { useState, useEffect, useCallback, useRef } from "react";
import { Session } from "@supabase/supabase-js";
import {
  createCrossDomainStorage,
  createCrossOriginMessaging,
} from "@/lib/utils/index";
import { getDomainInfo } from "@/lib/utils/domainDetection";
import { sessionUtils } from "@/lib/auth/sessionUtils";

export interface CrossDomainSessionState {
  session: Session | null;
  user: any | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  isConnected: boolean;
  lastSync: number | null;
  conflicts: string[];
  error: string | null;
}

export interface CrossDomainSessionActions {
  setSession: (session: Session | null) => void;
  clearSession: () => void;
  refreshSession: () => Promise<void>;
  syncSession: () => Promise<void>;
  resolveConflicts: (
    strategy?: "use_newest" | "use_cookie" | "use_storage" | "clear_all"
  ) => boolean;
  testConnectivity: () => Promise<{ [origin: string]: boolean }>;
}

export interface CrossDomainSessionOptions {
  enableDebug?: boolean;
  autoSync?: boolean;
  syncInterval?: number;
  onSessionChange?: (session: Session | null) => void;
  onError?: (error: Error) => void;
}

/**
 * Hook for managing cross-domain session state
 */
export function useCrossDomainSession(options: CrossDomainSessionOptions = {}) {
  const {
    enableDebug = process.env.NODE_ENV === "development",
    autoSync = true,
    syncInterval = 30000,
    onSessionChange,
    onError,
  } = options;

  const [state, setState] = useState<CrossDomainSessionState>({
    session: null,
    user: null,
    isLoading: true,
    isAuthenticated: false,
    isConnected: false,
    lastSync: null,
    conflicts: [],
    error: null,
  });

  const storageRef = useRef<ReturnType<typeof createCrossDomainStorage> | null>(
    null
  );
  const messagingRef = useRef<ReturnType<
    typeof createCrossOriginMessaging
  > | null>(null);
  const isInitializedRef = useRef(false);
  const syncIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Initialize cross-domain session management
  useEffect(() => {
    if (typeof window === "undefined") {
      setState((prev) => ({ ...prev, isLoading: false }));
      return;
    }

    const initializeSession = async () => {
      try {
        const domainInfo = getDomainInfo();

        // Only initialize in development or production environments
        if (!domainInfo.isDevelopment && !domainInfo.isProduction) {
          if (enableDebug) {
            console.log(
              "[useCrossDomainSession] Skipping initialization - not in supported environment"
            );
          }
          setState((prev) => ({ ...prev, isLoading: false }));
          return;
        }

        // Initialize storage
        storageRef.current = createCrossDomainStorage({
          enableDebug,
          storageKey: "SUPABASE_SESSION",
          cookieDomain: domainInfo.cookieDomain,
          enablePostMessage: domainInfo.isDevelopment,
        });

        // Initialize messaging
        messagingRef.current = createCrossOriginMessaging({
          enableDebug,
          enableAutoSync: autoSync,
          syncInterval,
        });

        // Set up message handlers
        messagingRef.current.addMessageHandler(handleCrossDomainMessage);

        // Start listening
        messagingRef.current.startListening();

        // Load initial session
        await loadInitialSession();

        // Set up periodic sync
        if (autoSync) {
          syncIntervalRef.current = setInterval(() => {
            syncSession();
          }, syncInterval);
        }

        isInitializedRef.current = true;
        setState((prev) => ({ ...prev, isLoading: false }));

        if (enableDebug) {
          console.log("[useCrossDomainSession] Initialized successfully");
        }
      } catch (error) {
        console.error("[useCrossDomainSession] Initialization error:", error);
        setState((prev) => ({
          ...prev,
          isLoading: false,
          error: error instanceof Error ? error.message : "Unknown error",
        }));
        onError?.(error as Error);
      }
    };

    initializeSession();

    return () => {
      cleanup();
    };
  }, [enableDebug, autoSync, syncInterval, onError]);

  // Listen for comprehensive-logout events (dispatched by cookie auth sign-out)
  useEffect(() => {
    if (typeof window === "undefined") return;

    const handleComprehensiveLogout = () => {
      setState((prev) => ({
        ...prev,
        session: null,
        user: null,
        isAuthenticated: false,
        error: null,
      }));
      if (storageRef.current) {
        storageRef.current.clearSession();
      }
      if (messagingRef.current) {
        messagingRef.current.broadcastSessionUpdate("SESSION_LOGOUT", null);
      }
      onSessionChange?.(null);
    };

    window.addEventListener("comprehensive-logout", handleComprehensiveLogout);
    return () => {
      window.removeEventListener(
        "comprehensive-logout",
        handleComprehensiveLogout
      );
    };
  }, [onSessionChange]);

  // Handle cross-domain messages
  const handleCrossDomainMessage = useCallback(
    (message: any) => {
      if (enableDebug) {
        console.log("[useCrossDomainSession] Received message:", message);
      }

      try {
        switch (message.action) {
          case "SESSION_LOGIN":
            handleSessionLogin(message);
            break;
          case "SESSION_LOGOUT":
            handleSessionLogout(message);
            break;
          case "SESSION_REFRESH":
            handleSessionRefresh(message);
            break;
          case "SESSION_SYNC":
            handleSessionSync(message);
            break;
        }
      } catch (error) {
        console.error("[useCrossDomainSession] Error handling message:", error);
        setState((prev) => ({
          ...prev,
          error: error instanceof Error ? error.message : "Unknown error",
        }));
        onError?.(error as Error);
      }
    },
    [enableDebug, onError]
  );

  // Handle session login from other domains
  const handleSessionLogin = useCallback(
    (message: any) => {
      if (message.data) {
        try {
          const sessionData = JSON.parse(message.data);

          setState((prev) => ({
            ...prev,
            session: sessionData,
            user: sessionData.user,
            isAuthenticated: true,
            error: null,
          }));

          // Update storage
          if (storageRef.current) {
            storageRef.current.setSessionData(sessionData);
          }

          // Update status
          updateStatus();

          // Notify parent
          onSessionChange?.(sessionData);

          if (enableDebug) {
            console.log(
              "[useCrossDomainSession] Session login synced from",
              message.source
            );
          }
        } catch (error) {
          console.error(
            "[useCrossDomainSession] Error parsing session data:",
            error
          );
        }
      }
    },
    [enableDebug, onSessionChange]
  );

  // Handle session logout from other domains
  const handleSessionLogout = useCallback(
    (message: any) => {
      setState((prev) => ({
        ...prev,
        session: null,
        user: null,
        isAuthenticated: false,
        error: null,
      }));

      // Clear storage
      if (storageRef.current) {
        storageRef.current.clearSession();
      }

      // Update status
      updateStatus();

      // Notify parent
      onSessionChange?.(null);

      if (enableDebug) {
        console.log(
          "[useCrossDomainSession] Session logout synced from",
          message.source
        );
      }
    },
    [enableDebug, onSessionChange]
  );

  // Handle session refresh from other domains
  const handleSessionRefresh = useCallback(
    (message: any) => {
      if (message.data) {
        try {
          const sessionData = JSON.parse(message.data);

          setState((prev) => ({
            ...prev,
            session: sessionData,
            user: sessionData.user,
            isAuthenticated: true,
            error: null,
          }));

          // Update storage
          if (storageRef.current) {
            storageRef.current.setSessionData(sessionData);
          }

          // Update status
          updateStatus();

          // Notify parent
          onSessionChange?.(sessionData);

          if (enableDebug) {
            console.log(
              "[useCrossDomainSession] Session refresh synced from",
              message.source
            );
          }
        } catch (error) {
          console.error(
            "[useCrossDomainSession] Error parsing session data:",
            error
          );
        }
      }
    },
    [enableDebug, onSessionChange]
  );

  // Handle session sync request
  const handleSessionSync = useCallback(
    (message: any) => {
      // Send current session to requesting domain
      if (storageRef.current && messagingRef.current && state.session) {
        messagingRef.current.broadcastSessionUpdate(
          "SESSION_LOGIN",
          JSON.stringify(state.session)
        );
      }

      if (enableDebug) {
        console.log(
          "[useCrossDomainSession] Session sync requested from",
          message.source
        );
      }
    },
    [state.session, enableDebug]
  );

  // Load initial session from storage
  const loadInitialSession = useCallback(async () => {
    try {
      if (!storageRef.current) return;

      const sessionData = storageRef.current.getSessionData();

      if (sessionData && storageRef.current.isSessionValid()) {
        setState((prev) => ({
          ...prev,
          session: sessionData,
          user: sessionData.user,
          isAuthenticated: true,
          error: null,
        }));

        if (enableDebug) {
          console.log(
            "[useCrossDomainSession] Loaded initial session for user:",
            sessionData.user?.id
          );
        }
      } else {
        // Clear invalid session
        storageRef.current.clearSession();
        setState((prev) => ({
          ...prev,
          session: null,
          user: null,
          isAuthenticated: false,
          error: null,
        }));
      }

      // Update status
      updateStatus();
    } catch (error) {
      console.error(
        "[useCrossDomainSession] Error loading initial session:",
        error
      );
      setState((prev) => ({
        ...prev,
        error: error instanceof Error ? error.message : "Unknown error",
      }));
    }
  }, [enableDebug]);

  // Update status information
  const updateStatus = useCallback(() => {
    try {
      const sessionStatus = sessionUtils.getCrossDomainSessionStatus();
      const connectionStatus = messagingRef.current?.getConnectionStatus();

      setState((prev) => ({
        ...prev,
        isConnected: connectionStatus?.isListening || false,
        lastSync: sessionStatus.lastSync,
        conflicts: sessionStatus.conflicts,
      }));
    } catch (error) {
      console.error("[useCrossDomainSession] Error updating status:", error);
    }
  }, []);

  // Set session
  const setSession = useCallback(
    (newSession: Session | null) => {
      setState((prev) => ({
        ...prev,
        session: newSession,
        user: newSession?.user || null,
        isAuthenticated: !!newSession,
        error: null,
      }));

      if (storageRef.current) {
        if (newSession) {
          storageRef.current.setSessionData(newSession);
        } else {
          storageRef.current.clearSession();
        }
      }

      // Broadcast to other domains
      if (messagingRef.current) {
        if (newSession) {
          messagingRef.current.broadcastSessionUpdate(
            "SESSION_LOGIN",
            JSON.stringify(newSession)
          );
        } else {
          messagingRef.current.broadcastSessionUpdate("SESSION_LOGOUT", null);
        }
      }

      // Update status
      updateStatus();

      // Notify parent
      onSessionChange?.(newSession);
    },
    [onSessionChange, updateStatus]
  );

  // Clear session
  const clearSession = useCallback(() => {
    setSession(null);
  }, [setSession]);

  // Refresh session
  const refreshSession = useCallback(async () => {
    try {
      if (!state.session) return;

      // Update expiration time (simulate refresh)
      const refreshedSession = {
        ...state.session,
        expires_at: Math.floor(Date.now() / 1000) + 3600, // 1 hour from now
      };

      setSession(refreshedSession);

      // Broadcast refresh to other domains
      if (messagingRef.current) {
        messagingRef.current.broadcastSessionUpdate(
          "SESSION_REFRESH",
          JSON.stringify(refreshedSession)
        );
      }

      if (enableDebug) {
        console.log("[useCrossDomainSession] Session refreshed");
      }
    } catch (error) {
      console.error("[useCrossDomainSession] Error refreshing session:", error);
      setState((prev) => ({
        ...prev,
        error: error instanceof Error ? error.message : "Unknown error",
      }));
    }
  }, [state.session, enableDebug, setSession]);

  // Sync session across domains
  const syncSession = useCallback(async () => {
    try {
      await sessionUtils.syncSessionAcrossDomains();
      updateStatus();

      if (enableDebug) {
        console.log("[useCrossDomainSession] Session synced");
      }
    } catch (error) {
      console.error("[useCrossDomainSession] Error syncing session:", error);
      setState((prev) => ({
        ...prev,
        error: error instanceof Error ? error.message : "Unknown error",
      }));
    }
  }, [enableDebug, updateStatus]);

  // Resolve session conflicts
  const resolveConflicts = useCallback(
    (
      strategy:
        | "use_newest"
        | "use_cookie"
        | "use_storage"
        | "clear_all" = "use_newest"
    ) => {
      try {
        const success = sessionUtils.resolveSessionConflicts(strategy);

        if (success) {
          // Reload session after resolving conflicts
          loadInitialSession();
        }

        return success;
      } catch (error) {
        console.error(
          "[useCrossDomainSession] Error resolving conflicts:",
          error
        );
        setState((prev) => ({
          ...prev,
          error: error instanceof Error ? error.message : "Unknown error",
        }));
        return false;
      }
    },
    [loadInitialSession]
  );

  // Test connectivity
  const testConnectivity = useCallback(async () => {
    try {
      return await sessionUtils.testCrossDomainConnectivity();
    } catch (error) {
      console.error(
        "[useCrossDomainSession] Error testing connectivity:",
        error
      );
      setState((prev) => ({
        ...prev,
        error: error instanceof Error ? error.message : "Unknown error",
      }));
      return {};
    }
  }, []);

  // Cleanup function
  const cleanup = useCallback(() => {
    if (syncIntervalRef.current) {
      clearInterval(syncIntervalRef.current);
      syncIntervalRef.current = null;
    }

    if (messagingRef.current) {
      messagingRef.current.destroy();
      messagingRef.current = null;
    }

    isInitializedRef.current = false;

    if (enableDebug) {
      console.log("[useCrossDomainSession] Cleaned up");
    }
  }, [enableDebug]);

  // Actions object
  const actions: CrossDomainSessionActions = {
    setSession,
    clearSession,
    refreshSession,
    syncSession,
    resolveConflicts,
    testConnectivity,
  };

  return {
    ...state,
    ...actions,
  };
}

/**
 * Hook for session state only
 */
export function useSession() {
  const { session, user, isLoading, isAuthenticated } = useCrossDomainSession();

  return {
    session,
    user,
    isLoading,
    isAuthenticated,
  };
}

/**
 * Hook for session actions only
 */
export function useSessionActions() {
  const { setSession, clearSession, refreshSession, syncSession } =
    useCrossDomainSession();

  return {
    setSession,
    clearSession,
    refreshSession,
    syncSession,
  };
}

/**
 * Hook for cross-domain status
 */
export function useCrossDomainStatus() {
  const { isConnected, lastSync, conflicts, error, testConnectivity } =
    useCrossDomainSession();

  return {
    isConnected,
    lastSync,
    conflicts,
    error,
    testConnectivity,
  };
}

/**
 * Hook for session debugging
 */
export function useSessionDebug() {
  const { getDebugInfo } = useCrossDomainSession();

  return {
    getDebugInfo: useCallback(() => {
      return sessionUtils.getDebugInfo();
    }, []),
  };
}

export default useCrossDomainSession;
